import styled, { css, keyframes } from 'styled-components';

import {
  BRAND_DEPLOYMENT_GLOW_STOPS,
  COLORS,
  HOME_STICKY_HEAD,
  NAV_DESKTOP,
  REDUCED_MOTION,
  STORYBOARD_CANVAS,
  STORYBOARD_COMPOSE,
} from '@/nova/theme/tokens';

/* One breath of the Lumio bloom on the active connector: two overlaid drop-shadows in the deployment aurora's
   pink + cyan swell and settle, so the connector INTO the beat being rendered softly pulsates in Lumio colours.
   On the edge's <g> (not its <path>), so it never clobbers React Flow's own `animated` dash-flow on the path. */
const edgeBloom = keyframes`
  0%,
  100% {
    filter: drop-shadow(0 0 1px ${STORYBOARD_CANVAS.lumioEdgeGlowPink})
      drop-shadow(0 0 1.5px ${STORYBOARD_CANVAS.lumioEdgeGlowCyan});
  }
  50% {
    filter: drop-shadow(0 0 4px ${STORYBOARD_CANVAS.lumioEdgeGlowPink})
      drop-shadow(0 0 5px ${STORYBOARD_CANVAS.lumioEdgeGlowCyan});
  }
`;

/* The React Flow surface. Theming is done through React Flow's own `--xy-*` CSS variables, all fed
   from theme tokens (no colour literals). Our custom node cards paint their own surface, so the
   default node chrome is neutralised to avoid a double border. */
export const CanvasFrame = styled.div<{ $immersive?: boolean }>`
  position: relative;
  width: 100%;
  /* Mobile / fallback: a fixed height (React Flow needs a definite one), with the toolbar/footer stacked
     around it in the normal-flow column. */
  height: 520px;
  ${NAV_DESKTOP} {
    /* Immersive (Miro-style) editor: the board is the SOLE child of the viewport-height composer Root and
       fills it edge-to-edge, with the toolbar + footer floating OVER it as pills (not stacked around it). The
       200px floor is only a short-viewport safety net; the board pans/zooms internally. */
    flex: 1 1 auto;
    min-height: 200px;
    height: auto;
  }
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.pageBg};

  /* Immersive (Miro-style full-bleed): drop the card border + radius so the dot-grid reaches every edge
     (the layout gutters + content card are collapsed in step, see MainLayout ImmersiveLayout). */
  ${(p) =>
    p.$immersive &&
    css`
      border: 0;
      border-radius: 0;
    `}

  --xy-background-color: ${COLORS.pageBg};
  --xy-node-background-color: ${COLORS.surfaceCard};
  --xy-node-border: 1px solid ${COLORS.panelBorder};
  --xy-node-border-radius: 14px;
  --xy-node-color: ${COLORS.text};
  /* Idle connectors read at textMuted (up from the near-invisible textFaint) so the storyboard's flow
     (key visual -> beat 1 -> ... -> finished ad, brand -> beat 1) is legible at rest, not just during a run. */
  --xy-edge-stroke: ${COLORS.textMuted};
  --xy-edge-stroke-width: 2;
  --xy-handle-background-color: ${COLORS.accent};
  --xy-handle-border-color: ${COLORS.surfaceCard};
  --xy-minimap-background-color: ${COLORS.surfaceCard};
  --xy-controls-button-background-color: ${COLORS.surfaceCard};
  --xy-controls-button-background-color-hover: ${COLORS.surfaceInset};
  --xy-controls-button-color: ${COLORS.text};
  --xy-controls-button-color-hover: ${COLORS.text};
  --xy-controls-button-border-color: ${COLORS.panelBorder};

  .react-flow__node {
    background: transparent;
    border: none;
    padding: 0;
    width: auto;
    color: ${COLORS.text};
    font-size: 13px;
  }

  /* Run choreography for the connectors. The active connector (leading INTO the node being worked, and
     within its beat segment) carries React Flow's built-in directional dash flow (\`animated\`) recoloured
     to Lumio pink with a soft pulsating bloom (below); connectors into completed nodes read as a subtle
     solid accent. Idle connectors keep the default neutral stroke. Kept subtle on purpose (see the repo
     working agreement on effects). */
  .react-flow__edge.sb-edge-done .react-flow__edge-path {
    stroke: ${COLORS.accent};
    opacity: 0.45;
  }

  .react-flow__edge.sb-edge-active .react-flow__edge-path {
    stroke: ${BRAND_DEPLOYMENT_GLOW_STOPS[1]};
  }

  /* The active connector softly pulsates in Lumio colours while its beat renders (bloom on the <g> so React
     Flow's dash-flow on the <path> is untouched). Kept subtle on purpose (see the effects working agreement). */
  .react-flow__edge.sb-edge-active {
    animation: ${edgeBloom} ${STORYBOARD_COMPOSE.EDGE_BLOOM_MS}ms ease-in-out infinite;
  }

  /* Connector into a beat/frame queued to be re-run by a regenerate/resume: a quiet dashed,
     desaturated caution-amber stroke matching the stale card border, so the two read as one state. */
  .react-flow__edge.sb-edge-stale .react-flow__edge-path {
    stroke: ${STORYBOARD_CANVAS.staleBorder};
    stroke-dasharray: 4 4;
  }

  ${REDUCED_MOTION} {
    .react-flow__edge.animated .react-flow__edge-path {
      animation: none;
    }
    .react-flow__edge.sb-edge-active {
      animation: none;
    }
  }
`;

/* The old full-canvas "Composing…" overlay (CanvasBusyOverlay + Spinner) was removed: the compose is now
   shown IN CONTEXT: the beat cards render as shimmer skeletons and type their motion prose in (see
   StoryboardCanvas `compose` → deriveGraph → BeatNode), so no opaque panel covers the board. */

/* Floating pill for the immersive combined bar (bottom-center Panel): the generate CTA + summary, with the run
   status / failure / result strip beneath. Capped height with internal scroll so a tall status never grows the
   pill past the board (`nowheel nopan` on the element keeps that scroll from panning the canvas). */
export const FooterPill = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 14px;
  border-radius: ${HOME_STICKY_HEAD.pillRadius};
  background: ${HOME_STICKY_HEAD.pillBg};
  border: 1px solid ${HOME_STICKY_HEAD.pillBorder};
  box-shadow: ${HOME_STICKY_HEAD.pillShadow};
  width: fit-content;
  max-width: min(1180px, calc(100vw - 40px));
  max-height: 46vh;
  overflow-y: auto;
`;
